import { CalendarClock, ClipboardList } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Fact } from '@/features/cases/components/shared/Fact'
import { useFormat } from '@/i18n/useFormat'
import { transitRange } from '@/lib/transit'
import type { CaseDetail } from '@/types/domain'

/* The deal's facts, as TWO cards of the overview's even grid (S-11 revision):
 * «Условия сделки» — what was agreed — and «Сроки и ответственные» — when and
 * by whom. They were one «Детали работы» card spanning the page, which is what
 * made the overview uneven: one card nine facts tall beside cards of three. The
 * transit, the incoterm and the entity are the quote's, carried onto the case
 * at conversion (P0-2). */

/** «Условия сделки»: the payment terms and method, the incoterm, the issuing
 *  entity and whose numbers the declared measures are. */
export function TermsCard({ record }: { record: CaseDetail }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList size={15} className="text-text-muted" aria-hidden />
          {t('cases.overview.terms')}
        </CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-3 pt-3 pb-4">
        {/* The terms as a NAMED kind rather than the phrase this card used to
            render (FR-32). A credit term shows its day count beside it, because
            "credit" without the number is not the agreement. */}
        <Fact label={t('cases.overview.paymentTerms')}>
          {record.paymentTermsKind
            ? [
                t(`cases.terms.${record.paymentTermsKind}`),
                record.paymentTermsCreditDays === undefined
                  ? undefined
                  : t('cases.overview.creditDays', {
                      count: record.paymentTermsCreditDays,
                    }),
              ]
                .filter(Boolean)
                .join(' · ')
            : '—'}
        </Fact>
        <Fact label={t('cases.overview.paymentMethod')}>
          {record.paymentMethod ? t(`cases.method.${record.paymentMethod}`) : '—'}
        </Fact>
        <Fact label={t('cases.overview.incoterm')}>{record.incoterm || '—'}</Fact>
        {/* `||`, not `??`: the key is absent when the entity row is gone, and
            a blank string is not an answer this card can print either. */}
        <Fact label={t('cases.overview.entity')}>{record.entityName || '—'}</Fact>
        {/* Whose numbers the declared measures are (FR-12), carried over from
            the quote at conversion. Never a dash: both states are a real answer,
            and "our estimate" is the one Operations has to know BEFORE the truck
            is booked, so it is stated rather than left to the absence of a
            badge. Worded as the fact it reports, not as a checkbox label. */}
        <Fact label={t('cases.overview.packingList')}>
          {record.finalPackingListReceived
            ? t('cases.overview.packingListReceived')
            : t('cases.overview.packingListEstimate')}
        </Fact>
      </CardBody>
    </Card>
  )
}

/** «Сроки и ответственные»: the dates, the transit the quote promised, who
 *  answers for the case, and the note the client actually cares about. */
export function TimingCard({ record }: { record: CaseDetail }) {
  const { t } = useTranslation()
  const format = useFormat()

  /* ATD once the cargo has moved, ETD while departure is still a plan — the
     label names which fact the date is, instead of a "departure" that could be
     either. ATD/ETD/ETA stay untranslated like the header band: they are the
     trade's own abbreviations in both offices. */
  const departure = record.atd
    ? `ATD ${format.date(record.atd)}`
    : record.etd
      ? `ETD ${format.date(record.etd)}`
      : null
  const dates = [departure, record.eta ? `ETA ${format.date(record.eta)}` : null]
    .filter(Boolean)
    .join(' · ')

  /* The range the quote promised, worded by the same helper the quote's route
     options use, so one agreement reads the same on both records. */
  const transit = transitRange(
    { min: record.transitDaysMin, max: record.transitDaysMax },
    t,
  )

  const responsible = [
    record.ownerName,
    record.opsOwnerName
      ? t('cases.overview.opsNamed', { name: record.opsOwnerName })
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock size={15} className="text-text-muted" aria-hidden />
          {t('cases.overview.schedule')}
        </CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-3 pt-3 pb-4">
        <Fact label={t('cases.overview.dates')}>{dates || '—'}</Fact>
        <Fact label={t('cases.overview.transit')}>
          {transit ? <span className="asa-num">{transit}</span> : '—'}
        </Fact>
        <Fact label={t('cases.overview.responsible')}>{responsible || '—'}</Fact>
        <Fact label={t('cases.overview.notes')}>{record.notes ?? '—'}</Fact>
      </CardBody>
    </Card>
  )
}
